import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, Tooltip, Skeleton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import apiClient from '../services/apiClient';
import { API_URL } from '../config';
import { typography } from '~/theme';

interface BuildInfo {
  branch: string;
  build_number: string;
  build_time: string;
  commit_hash: string;
  commit_author: string;
  environment: string;
}

interface BuildInfoPanelProps {
  /** 'expanded' shows full details; 'compact' shows a single-line footer badge */
  variant?: 'expanded' | 'compact';
}

const BuildInfoPanel: React.FC<BuildInfoPanelProps> = ({ variant = 'expanded' }) => {
  const [info, setInfo] = useState<BuildInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get(`${API_URL}/build-info`)
      .then((res) => setInfo(res.data))
      .catch(() =>
        setInfo({
          branch: 'local',
          build_number: 'dev',
          build_time: 'unknown',
          commit_hash: 'unknown',
          commit_author: 'unknown',
          environment: 'Local Development Build',
        }),
      )
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return variant === 'compact' ? (
      <Skeleton width={180} height={20} />
    ) : (
      <Skeleton variant="rounded" width="100%" height={80} />
    );
  }

  if (!info) return null;

  const isSandbox = info.environment === 'sandbox';

  const formatTime = (iso: string) => {
    if (!iso || iso === 'unknown') return 'unknown';
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
      });
    } catch {
      return iso;
    }
  };

  const shortHash = info.commit_hash?.substring(0, 7) ?? 'unknown';

  // ── Compact variant: single-line footer badge ──────────────────────
  if (variant === 'compact') {
    const label = isSandbox
      ? `${info.branch}@${shortHash} · #${info.build_number}`
      : 'Local Dev Build';

    return (
      <Tooltip
        title={
          <Box sx={{ fontSize: typography.caption.fontSize, lineHeight: typography.body2.lineHeight }}>
            <div><strong>Branch:</strong> {info.branch}</div>
            <div><strong>Commit:</strong> {info.commit_hash}</div>
            <div><strong>Build:</strong> #{info.build_number}</div>
            <div><strong>Author:</strong> {info.commit_author}</div>
            <div><strong>Built:</strong> {formatTime(info.build_time)}</div>
          </Box>
        }
        arrow
        placement="top"
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'default',
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.disabled" noWrap>
            {label}
          </Typography>
          {isSandbox && (
            <Chip
              label="SANDBOX"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: 16, fontSize: typography.overline.fontSize, fontWeight: typography.h1.fontWeight }}
            />
          )}
        </Box>
      </Tooltip>
    );
  }

  // ── Expanded variant: full info panel ──────────────────────────────
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: isSandbox ? 'warning.light' : 'divider',
        bgcolor: isSandbox ? 'rgba(237, 108, 2, 0.04)' : 'action.hover',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <InfoOutlinedIcon fontSize="small" color={isSandbox ? 'warning' : 'action'} />
        <Typography variant="caption" fontWeight={typography.h1.fontWeight} textTransform="uppercase" letterSpacing={0.5}>
          {isSandbox ? 'Sandbox Build Info' : 'Local Development Build'}
        </Typography>
        {isSandbox && (
          <Chip
            label="SANDBOX BUILD"
            size="small"
            color="warning"
            variant="outlined"
            sx={{ height: 18, fontSize: typography.overline.fontSize, fontWeight: typography.h1.fontWeight }}
          />
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: '2px 12px',
          fontSize: typography.caption.fontSize,
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={typography.label.fontWeight}>Branch</Typography>
        <Typography variant="caption" fontFamily="monospace">{info.branch}</Typography>

        <Typography variant="caption" color="text.secondary" fontWeight={typography.label.fontWeight}>Build</Typography>
        <Typography variant="caption" fontFamily="monospace">#{info.build_number}</Typography>

        <Typography variant="caption" color="text.secondary" fontWeight={typography.label.fontWeight}>Commit</Typography>
        <Typography variant="caption" fontFamily="monospace">{shortHash}</Typography>

        <Typography variant="caption" color="text.secondary" fontWeight={typography.label.fontWeight}>Author</Typography>
        <Typography variant="caption">{info.commit_author}</Typography>

        <Typography variant="caption" color="text.secondary" fontWeight={typography.label.fontWeight}>Built At</Typography>
        <Typography variant="caption">{formatTime(info.build_time)}</Typography>
      </Box>
    </Box>
  );
};

export default BuildInfoPanel;
